import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { RiLoader4Line } from '@remixicon/react'
import { type VariantProps } from 'tailwind-variants'
import { cx, focusRing, tv } from '../../lib/utils'

const buttonVariants = tv({
  base: [
    'relative inline-flex items-center justify-center whitespace-nowrap rounded-lg border px-3 py-2 text-center text-sm font-medium shadow-none transition-all duration-150 ease-out',
    'disabled:pointer-events-none disabled:shadow-none',
    focusRing,
  ],
  variants: {
    variant: {
      primary: [
        'border-transparent',
        'text-white',
        // 600 rather than 500: the brand's blue is bright enough that white
        // on 500 gives up contrast, and starting at 600 leaves hover a step.
        'bg-primary-600 dark:bg-gray-50 dark:text-primary-900',
        'hover:bg-primary-900 dark:hover:bg-gray-200',
        'disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-400',
      ],
      // Ambra: una sola per schermata (brand kit §6.8). Testo navy-900, mai bianco.
      cta: [
        'border-transparent',
        'bg-amber-500 text-primary-900',
        'hover:bg-amber-700 hover:text-white',
        'disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800',
      ],
      secondary: [
        'border-primary-600 dark:border-gray-300',
        'text-primary-600 dark:text-gray-50',
        'bg-transparent shadow-none',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        'disabled:text-gray-400 dark:disabled:text-gray-600',
      ],
      ghost: [
        'shadow-none',
        'border-transparent',
        'text-gray-900 dark:text-gray-50',
        'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800/80',
        'disabled:text-gray-400 dark:disabled:text-gray-600',
      ],
      destructive: [
        'text-white',
        'border-transparent',
        'bg-red-600 dark:bg-red-600',
        'hover:bg-red-700 dark:hover:bg-red-700',
        'disabled:bg-red-300 disabled:text-white dark:disabled:bg-red-950 dark:disabled:text-red-400',
      ],
    },
    size: {
      sm: 'h-8 px-2.5 text-xs',
      md: 'h-9 px-3 text-sm',
      lg: 'h-10 px-4 text-base',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

interface ButtonProps
  extends React.ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  icon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild,
      loading = false,
      loadingText,
      className,
      disabled,
      variant,
      size,
      children,
      icon,
      ...props
    }: ButtonProps,
    forwardedRef,
  ) => {
    const Component = asChild ? Slot : 'button'
    return (
      <Component
        ref={forwardedRef}
        className={cx(buttonVariants({ variant, size }), className)}
        disabled={loading || disabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <RiLoader4Line
              className="mr-2 size-4 shrink-0 animate-spin"
              aria-hidden="true"
            />
            {loadingText ?? children}
          </>
        ) : (
          <>
            {icon && <span className="mr-2 size-4 shrink-0">{icon}</span>}
            {children}
          </>
        )}
      </Component>
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants, type ButtonProps }
export default Button
